import type { PhoneMatch } from '@/types/game'

/** Spielregeln aus dem Epic-Briefing - verbindliche Konstanten. */
export const BUDGET = 100
export const STAKE = 10
/** 5 % Wettsteuer gehen von der Auszahlung ab. */
export const TAX_FACTOR = 0.95

export const GUESS_MIN = -100
export const GUESS_MAX = 200
export const CONFIDENCE_MIN = 1
export const CONFIDENCE_MAX = 5
export const KNOWLEDGE_MIN = 0
export const KNOWLEDGE_MAX = 10

/** Auszahlung eines Treffers: Einsatz × Quote × 0,95, kaufmännisch auf Cent. */
export function payout(odds: number, stake: number = STAKE): number {
    return Math.round(stake * odds * TAX_FACTOR * 100) / 100
}

/** Reingewinn eines Treffers - das, was am Ende mehr in der Tasche ist. */
export function profit(odds: number, stake: number = STAKE): number {
    return Math.round((payout(odds, stake) - stake) * 100) / 100
}

/**
 * Die Auszahlung vor der Wettsteuer - also das, was die Quote verspricht.
 *
 * Diese Zahl steht nirgends auf dem Wettschein, ist aber die einzige, die
 * Schueler:innen im Kopf haben: Quote 2,50 mal 10 Euro sind 25 Euro. Erst der
 * Vergleich mit der tatsaechlichen Auszahlung macht die Steuer sichtbar.
 */
export function grossPayout(odds: number, stake: number = STAKE): number {
    return Math.round(stake * odds * 100) / 100
}

/** Was die Wettsteuer von einer einzelnen Auszahlung wegnimmt. */
export function tax(odds: number, stake: number = STAKE): number {
    return Math.round((grossPayout(odds, stake) - payout(odds, stake)) * 100) / 100
}

/**
 * Die Wettsteuer ueber die ganze Runde.
 *
 * Bewusst aus den einzelnen Treffern aufsummiert und nicht aus der
 * Gesamtauszahlung zurueckgerechnet: Sonst weicht die Zahl durch die
 * Cent-Rundung je Spiel von der Summe der angezeigten Einzelbetraege ab, und
 * genau die rechnet eine Klasse nach.
 */
export function totalTax(matches: PhoneMatch[]): number {
    const sum = matches.reduce((acc, match) => {
        if (match.pick === null || match.pick === 'skip' || match.pick !== match.result) {
            return acc
        }

        return acc + tax(match.odds[match.pick])
    }, 0)

    return Math.round(sum * 100) / 100
}

/**
 * Das verbleibende Budget.
 *
 * Nur platzierte Wetten kosten - ein bewusstes Auslassen ist gratis. Genau
 * darum geht es in der Stunde: Nicht-Spielen ist die einzige Strategie, die
 * nicht verliert.
 */
export function remainingBudget(matches: PhoneMatch[]): number {
    const placed = matches.filter((match) => match.pick !== null && match.pick !== 'skip').length

    return BUDGET - placed * STAKE
}

/** Wie viele Spiele bereits eine Entscheidung haben (Tipp oder Auslassen). */
export function decidedCount(matches: PhoneMatch[]): number {
    return matches.filter((match) => match.pick !== null).length
}

/** Begrenzt einen Wert auf einen Bereich. */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value))
}

/**
 * Wie weit lag die Schätzung daneben?
 *
 * Positiv heißt überschätzt - es kam weniger heraus als gedacht. Genau das ist
 * die Lernerfahrung, deshalb wird die Richtung farblich unterschieden.
 */
export function estimateDeviation(guess: number, actual: number): number {
    return Math.round((guess - actual) * 100) / 100
}

export type EstimateVerdict = 'over' | 'close' | 'under'

/** Toleranz, innerhalb derer eine Schätzung als richtig gilt. */
export const ESTIMATE_TOLERANCE = 10

export function estimateVerdict(guess: number, actual: number): EstimateVerdict {
    const deviation = estimateDeviation(guess, actual)

    if (deviation > ESTIMATE_TOLERANCE) {
        return 'over'
    }

    if (deviation < -ESTIMATE_TOLERANCE) {
        return 'under'
    }

    return 'close'
}
